import { useMemo } from "react";
import {
  Bar,
  BarChart,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DateTime } from "luxon";
import { formatMoney } from "../../utils/formatMoney";
import { useGetStatsQuery } from "../../query/statsQuery";
import type { StatsPeriod } from "../../api/statsApi";
import type { StatsScope } from "../../hooks/useStatsScope";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import { LoadingContainer } from "../../components/LoadingContainer";
import colors from "@styling/colors";
import { borderRadius } from "@styling/sizing";
import { useTranslation } from "react-i18next";

interface PeriodBarChartProps {
  scope: StatsScope;
  from: string;
  to: string;
  period: StatsPeriod;
  onSelect: (item: string) => void;
  selectedItem?: string;
}

export const PeriodBarChart = ({
  scope,
  from,
  to,
  period,
  onSelect,
  selectedItem,
}: PeriodBarChartProps) => {
  const { t } = useTranslation();
  const { data: stats, isLoading } = useGetStatsQuery(
    scope,
    period,
    from,
    to
  );

  const chartData = useMemo(() => {
    if (period === "weeks") {
      return (stats ?? []).map((s) => {
        const weekNumber = parseInt(s.id.match(/W(\d+)/)?.[1] ?? "0", 10);
        return {
          id: s.id,
          value: s.receipts.grossAmount.amount,
          name: t("statistics.weekLabel", { number: weekNumber }),
          sublabel: formatMoney(s.receipts.grossAmount),
        };
      });
    }
    if (period === "months") {
      return (stats ?? []).map((s) => {
        const date = DateTime.fromFormat(`${s.id}-01`, "yyyy-MM-dd");
        return {
          id: s.id,
          value: s.receipts.grossAmount.amount,
          name: date.toFormat("MMM yyyy"),
          sublabel: formatMoney(s.receipts.grossAmount),
        };
      });
    }
    return (stats ?? []).map((s) => {
      const date = DateTime.fromISO(s.id);
      return {
        id: s.id,
        value: s.receipts.grossAmount.amount,
        name: date.toFormat("EEE d"),
        sublabel: formatMoney(s.receipts.grossAmount),
      };
    });
  }, [stats, period, t]);

  if (isLoading) {
    return <LoadingContainer message={t("statistics.loading")} />;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={chartData}
        margin={{ top: 16, right: 16, left: 0, bottom: 0 }}
      >
        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
        <YAxis
          tickFormatter={(v) => formatMoney({ amount: v, currency: Currency.SEK })}
          tick={{ fontSize: 12 }}
        />
        <ReferenceLine y={0} stroke={colors.border} strokeWidth={1} />
        <Tooltip
          formatter={(value: number) =>
            formatMoney({ amount: value, currency: Currency.SEK })
          }
          cursor={false}
        />
        <Bar
          dataKey="value"
          onClick={(entry: { id: string }) => onSelect(entry.id)}
          cursor="pointer"
          radius={[borderRadius.regular, borderRadius.regular, 0, 0]}
        >
          {chartData.map((entry) => (
            <Cell
              key={entry.id}
              fill={
                selectedItem === entry.id ? colors.secondary : colors.primary
              }
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};
